import { useMemo } from 'react';
import type { ScheduledCoupon } from '@/types/collection.types';
import { formatCurrencyMxn } from '@/features/collection/utils/couponFormatting';
import { getDaysUntilDue } from '@/features/collection/utils/couponUrgency';

export type RangeFilter = '7d' | '30d' | 'all';

export interface AgendadosHeaderProps {
    allCoupons: ScheduledCoupon[];
    today: string;
    activeRange: RangeFilter;
    onRangeChange: (next: RangeFilter) => void;
    page: number;
    totalPages: number;
    totalCouponsInRange: number;
    onPageChange: (next: number) => void;
}

interface Kpi {
    key: RangeFilter;
    label: string;
    monto: number;
    count: number;
}

function computeKpis(allCoupons: ScheduledCoupon[], today: string): Kpi[] {
    const within = (days: number) => allCoupons.filter(c => {
        const d = getDaysUntilDue(c.due_date, today);
        return d >= 0 && d <= days;
    });
    const c7 = within(7);
    const c30 = within(30);
    return [
        { key: '7d', label: 'Próximos 7d', monto: c7.reduce((s, c) => s + c.pending_amount_mxn, 0), count: c7.length },
        { key: '30d', label: 'Próximos 30d', monto: c30.reduce((s, c) => s + c.pending_amount_mxn, 0), count: c30.length },
        { key: 'all', label: 'Todo futuro', monto: allCoupons.reduce((s, c) => s + c.pending_amount_mxn, 0), count: allCoupons.length },
    ];
}

export function AgendadosHeader({
    allCoupons, today, activeRange, onRangeChange,
    page, totalPages, totalCouponsInRange, onPageChange,
}: AgendadosHeaderProps) {
    const kpis = useMemo(() => computeKpis(allCoupons, today), [allCoupons, today]);

    return (
        <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-1 flex-wrap gap-3">
                {kpis.map(k => {
                    const isActive = k.key === activeRange;
                    return (
                        <button
                            key={k.key}
                            onClick={() => onRangeChange(k.key)}
                            className={`min-w-[140px] rounded border p-3 text-left transition ${
                                isActive
                                    ? 'border-cyan-500 bg-cyan-500/10'
                                    : 'border-slate-700 bg-slate-900 hover:border-slate-500'
                            }`}
                        >
                            <div className="text-xs uppercase text-slate-400">{k.label}</div>
                            <div className="mt-1 text-lg font-semibold text-slate-100">{formatCurrencyMxn(k.monto)}</div>
                            <div className="text-xs text-slate-400">{k.count} cupones</div>
                        </button>
                    );
                })}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-400">
                <span>{page} / {totalPages || 1}</span>
                <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    className="rounded px-2 py-1 hover:bg-slate-800 disabled:opacity-30"
                    aria-label="Página anterior"
                >‹</button>
                <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages}
                    className="rounded px-2 py-1 hover:bg-slate-800 disabled:opacity-30"
                    aria-label="Página siguiente"
                >›</button>
                <span className="ml-2 text-xs">{totalCouponsInRange} total</span>
            </div>
        </div>
    );
}
